import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BillingV2Service } from '@core/services/lingo2-account/billing-v2.service';
import { environment } from '@env/environment';
import {
  BillingApiChargeResponseDto,
  BillingApiMerchantResponseDto,
  BillingApiPaymentResponseDto,
} from '@lingo2-billing-sdk/models';
import { BillingCorePaymentStatusEnum } from '@lingo2-billing-sdk/models/src/billing-core/billing-core-payment/types/enums/billing-core-payment-status.enum';
import { ChangableComponent } from '@models/changable.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import { takeUntil, tap, Subscription } from 'rxjs';
import { PaymentFail } from '../models';
import { PaymentWidgetMerchantProcessingService } from './payment-widget-merchant-processing.service';
import {ErrorNotificationService} from "@shared/error-notification/error-notification.service";

/**
 * Компонент исполнения запроса на оплату и взаимодействия с UI мерчанта (если требуется).
 * Компоненту делегированы следующие функции:
 * - отправка запроса на оплату и отслеживание статуса платежа;
 * - ввод данных новой карты;
 * - обработка 3DSecure и других действий требующих внимания покупателя;
 * - прочие, требующие внимания покупателя действия (например, оплата по QR-коду).
 */
@Component({
  selector: 'app-payment-widget-merchant-processing',
  templateUrl: './payment-widget-merchant-processing.component.html',
  styleUrls: ['./payment-widget-merchant-processing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentWidgetMerchantProcessingComponent extends ChangableComponent implements OnInit {
  @Input() public set payment(payment: BillingApiChargeResponseDto | BillingApiPaymentResponseDto) {
    if (payment['payment']) {
      this._payment = payment as BillingApiChargeResponseDto;
      this._isCharge = true;
    } else {
      this._payment = payment as BillingApiPaymentResponseDto;
      this._isCharge = false;
    }
  }

  public get payment() {
    return this._payment;
  }

  @Input()
  public set isAccountPayment(value: boolean) {
    this._isAccountPayment = value;
  }

  public get isAccountPayment() {
    return this._isAccountPayment;
  }

  @Input() public operation: 'pay' | 'add-card';

  @Input() public merchant: BillingApiMerchantResponseDto;

  @Output() public succeed = new EventEmitter<boolean>();
  @Output() public canceled = new EventEmitter<boolean>();
  @Output() public failed = new EventEmitter<PaymentFail>();
  @Output() public processing = new EventEmitter<boolean>();

  public error: string;
  public _isAccountPayment = true;
  public loading = true;
  public loaded = true;
  public safePaymentUrl: SafeResourceUrl;
  public _payment: BillingApiChargeResponseDto | BillingApiPaymentResponseDto;
  public _isCharge = true;
  private paymentUrl: string;
  private listenForFinish$$: Subscription;

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    private billingV2Service: BillingV2Service,
    private merchantService: PaymentWidgetMerchantProcessingService,
    private sanitizer: DomSanitizer,
    public deviceService: DeviceDetectorService,
    protected cdr: ChangeDetectorRef,
  ) {
    super(cdr);
  }

  public ngOnInit() {
    this.merchantService.pay$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => this.pay(),
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      }
    });
  }

  public get paymentAsCharge() {
    return this.payment as BillingApiChargeResponseDto;
  }

  public get isCharge() {
    return this._isCharge;
  }

  public get isPayment() {
    return this._isCharge;
  }

  public get paymentAsPayment() {
    return this.payment as BillingApiPaymentResponseDto;
  }

  public get isAddCard() {
    return this.operation === 'add-card';
  }

  public get isPay() {
    return this.operation === 'pay';
  }

  private pay() {
    console.warn('PaymentWidgetMerchantProcessingComponent.pay');
    if (this.isCharge) {
      if (!this.paymentAsCharge || !this.paymentAsCharge.payment.paymentUrl) {
        if (!this.isAccountPayment) {
          this.failed.emit({ message: 'No Payment url! Payment id: ' + this.paymentAsCharge.payment.id });
          return;
        }
      }

      this.setPaymentUrl(this.paymentAsCharge.payment.paymentUrl);
      this.detectChanges();
    } else {
      if (!this.paymentAsPayment || !this.paymentAsPayment.paymentUrl) {
        if (!this.isAccountPayment) {
          this.failed.emit({ message: 'No Payment url! Payment id: ' + this.paymentAsCharge.payment.id });
          return;
        }
      }
      this.setPaymentUrl(this.paymentAsPayment.paymentUrl);
      this.detectChanges();
    }

    if (this.isCharge) {
      this.listenForFinish(this.paymentAsCharge.payment.id);
    } else {
      this.listenForFinish(this.paymentAsPayment.id);
    }
  }

  private setPaymentUrl(paymentUrl: string) {
    if (this.paymentUrl !== paymentUrl) {
      this.paymentUrl = paymentUrl;
      this.safePaymentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.checkUrl(paymentUrl));
    }
  }

  public checkUrl(url: string): string {
    if (!url) {
      return null;
    }
    const isAbsolute = url.indexOf('http://') === 0 || url.indexOf('https://') === 0 || url.indexOf('//') === 0;
    return isAbsolute
      ? url
      : url.indexOf('/') === 0
      ? environment.stripe_iframe_host + url
      : environment.stripe_iframe_host + '/' + url;
  }

  private listenForFinish(paymentId: string) {
    if (!paymentId) {
      return;
    }

    this.listenForFinish$$?.unsubscribe();
    this.listenForFinish$$ = this.billingV2Service
      .getPayment(paymentId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (data) => {
          switch (data.status) {
            case BillingCorePaymentStatusEnum.SUCCEEDED:
              this.succeed.emit(true);
              break;
            default:
              this.setTimeout(() => {
                this.listenForFinish(paymentId);
              }, 2000);
              break;
          }
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.failed.emit({ message: `${error}` });
        }
      });
  }

  public get isStripe() {
    return this.merchant && this.merchant.name.toLowerCase().indexOf('stripe') !== -1;
  }

  public get isTinkoff() {
    return this.merchant && this.merchant.name.toLowerCase().indexOf('tinkoff') !== -1;
  }

  public watchFrameLoad() {
    const frame = document.getElementById('payment-frame') as HTMLIFrameElement;
    if (this.isStripe) {
      frame.style.height = '550px';
    } else if (this.isTinkoff) {
      frame.style.height = '720px';
    } else {
      frame.style.height = '550px';
    }
  }
}
